<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { BG, classArt, fightPose } from '../game/art';
import { classLabel } from '../game/hero-api';
import FightScreen from './FightScreen.vue';
import { fetchArenaStats, type ArenaStats } from '../game/arena-api';
import { fetchCurrentFight, type FightView } from '../game/mobfight-api';
import { pvpQueue, startSearch, cancelQueue, acceptQueue } from '../game/pvp-queue';
import type { PvpStage } from '../game/pvp-stage';
import { confirmAction } from '../game/confirm';
import cashIcon from '../assets/fimbulwake/icons/cash.webp';
import nerveIcon from '../assets/fimbulwake/icons/nerve.webp';
import { t } from '../i18n';

// Bubble nerve/cash changes up so HeroScreen refreshes the header.
const props = defineProps<{ heroClass?: string }>();
const emit = defineEmits<{
  (e: 'updated'): void;
  (e: 'locked', stage: PvpStage): void;
}>();

const loading = ref(true);
const error = ref('');
const stats = ref<ArenaStats | null>(null);
const fightInitial = ref<FightView | null>(null); // a bout we started or resumed

/**
 * The queue itself lives in `pvp-queue.ts`, NOT here — leaving this screen no
 * longer kills the search (CU-86cb9yd3p follow-up: Dota-style, the queue rides
 * along in the corner dock while the player wanders the app). The Pit renders
 * the same store the dock does; the only state that is genuinely this
 * screen's own is the live-bout handoff to FightScreen.
 */
const fighting = ref(false);
const state = computed(() => (fighting.value ? 'fighting' : pvpQueue.phase));
const opponent = computed(() => (pvpQueue.phase === 'found' ? pvpQueue.opponent : null));
const flowError = computed(() => pvpQueue.error);
/** "Walked away · −50 silver" — a receipt, not an error. */
const cancelledNotice = computed(() => pvpQueue.notice);
const busy = computed(() => pvpQueue.busy);

/**
 * Only a LIVE bout holds the player now (QA 86cb1qvpt — it is PvP). A merely
 * found foe travels with the queue dock instead of locking the shell: walking
 * to another screen neither cancels nor charges anything, the hold just keeps
 * counting in the corner. The fee applies only to an actual CANCEL (or is
 * dodged entirely by letting the hold lapse — the server's rule, not ours).
 */
const stage = computed<PvpStage>(() => (fighting.value ? 'fighting' : null));
function warnUnload(e: BeforeUnloadEvent) {
  e.preventDefault();
  // Chrome still wants a returnValue to show the native dialog.
  e.returnValue = '';
}
watch(stage, (s) => {
  emit('locked', s);
  if (s === 'fighting') window.addEventListener('beforeunload', warnUnload);
  else window.removeEventListener('beforeunload', warnUnload);
}, { immediate: true });
onUnmounted(() => {
  emit('locked', null);
  window.removeEventListener('beforeunload', warnUnload);
});

/** What dropping this foe costs (rating), straight from the server's stakes. */
const cancelRating = computed(() => stats.value?.stakes.cancelRating ?? 0);

/** Elapsed honest seconds in the queue — the store ticks `now`. */
const searchSeconds = computed(() =>
  pvpQueue.startedAt > 0 ? Math.max(0, Math.floor((pvpQueue.now - pvpQueue.startedAt) / 1000)) : 0);

/** The accept window, off the server's own deadline (QA 86cb9yd3p item 6). */
const holdLeft = computed(() => {
  const total = Math.max(0, Math.ceil((pvpQueue.expiresAt - pvpQueue.now) / 1000));
  return { m: Math.floor(total / 60), s: String(total % 60).padStart(2, '0'), total };
});
/** Under a minute left — the card warns rather than merely counting. */
const holdUrgent = computed(() => holdLeft.value.total > 0 && holdLeft.value.total <= 60);

async function load() {
  try {
    stats.value = await fetchArenaStats();
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    loading.value = false;
  }
}

/**
 * Walk straight back into an unfinished bout.
 *
 * Leaving the fight screen (switching tabs, a stray click) does NOT abandon the
 * fight — the session stays live server-side for its TTL. Without this the
 * player was locked out of their own Pit: /arena/search refuses while any bout
 * is open ("Finish your current fight first.") and there was no way back in, so
 * they just waited out the timer. Now we ask on mount, and if the live bout is
 * an arena one we drop them right back on the screen.
 */
async function resumeIfFighting() {
  try {
    const live = await fetchCurrentFight();
    if (live?.active && live.kind === 'arena') {
      fightInitial.value = live;
      fighting.value = true;
    }
  } catch { /* nothing live — stay in the lobby */ }
}

onMounted(() => { void load(); void resumeIfFighting(); });

/** Enter the Pit: hand matchmaking to the queue store (it survives leaving). */
function enter() {
  void startSearch(stats.value?.stakes.cancelRating ?? 0);
}

/** Leave the queue mid-search — free, the foe is not revealed yet. */
function leaveQueue() {
  void cancelQueue();
}

/** FIGHT: accept the held match — spends nerve, starts the bout — then hand
 *  off to FightScreen, which resumes that SAME live session (it never
 *  starts a second one; see mobfight.service's live-session guard), so this
 *  never double-charges nerve or re-rolls the opponent. */
async function fight() {
  const foe = opponent.value;
  if (!foe || busy.value) return;
  // The calling card, same rail as the City Map's pre-fight confirm
  // (CU-86cbc65ny item 5: every fight is confirmed with a pop-up — the
  // Holmgang too). Declining is NOT a cancel: no fee, the hold just keeps
  // counting on the found card behind the dialog.
  const s = stats.value?.stakes;
  const go = await confirmAction({
    kicker: t('battle.confirm.kicker'),
    title: t('arena.confirm.title', { name: foe.name }),
    rows: [
      { label: t('arena.confirm.foe'), value: `${t('arena.foeLevel', { level: foe.level })} · ${t('arena.rating', { rating: foe.rating })}`, tone: 'dim' },
      ...(s ? [{ label: t('arena.stakes.costs'), value: `${s.nerveCost} ${t('arena.stakes.fury')}`, icon: nerveIcon }] : []),
      ...(s ? [{ label: t('arena.stakes.win'), value: `+${s.winGold.toLocaleString()} ${t('arena.stakes.silver')} · +${foe.winRating} ${t('arena.stakes.rating')}`, icon: cashIcon, tone: 'success' as const }] : []),
      ...(s ? [{ label: t('arena.stakes.lose'), value: `−${s.lossGold.toLocaleString()} ${t('arena.stakes.silver')} · −${Math.abs(foe.lossRating)} ${t('arena.stakes.rating')}`, tone: 'error' as const }] : []),
    ],
    note: t('arena.confirm.note'),
    confirmLabel: t('arena.fight'),
    cancelLabel: t('arena.confirm.later'),
  });
  if (!go || busy.value) return;
  try {
    fightInitial.value = await acceptQueue(); // the bout we just paid for
    fighting.value = true;
  } catch { /* refused (hold lapsed in the race) — the store already says so */ }
}

/** CANCEL: drop the held foe, back to idle. Costs `stakes.cancelRating` points
 *  of rating — the button says so before it is clicked, and the lobby then
 *  states what was actually taken (which at the bottom of the ladder is less
 *  than the nominal penalty). */
async function cancelFound() {
  if (busy.value) return;
  const ratingLost = await cancelQueue();
  if (ratingLost > 0) { emit('updated'); void load(); } // the standing just moved — re-read it
}

async function onDone() {
  fighting.value = false;
  fightInitial.value = null; // stale view must not be re-fed into the next bout
  await load();
  emit('updated');
}

// NO unmount cancel — that hook is exactly what used to kill the queue on
// navigation. The store keeps searching; the corner dock takes over the view.

// ── Presentational only — no state/logic, just which art to stand in the
//    ring. fightPose gives the full-body combat pose; classArt (a portrait
//    crop) is the fallback for any class that ever ships without one. ──────
const youArt = computed(() => fightPose(props.heroClass) || classArt(props.heroClass));
const foeArt = (cls?: string | null) => fightPose(cls) || classArt(cls);
</script>

<template>
  <section>
    <!-- Fighting: hand off to the shared interactive tactical battle in arena mode -->
    <FightScreen
      v-if="state === 'fighting'"
      :arena="true"
      :hero-class="props.heroClass"
      :initial="fightInitial"
      @done="onDone"
    />

    <template v-else>
      <!-- No scr-head banner here: the ring IS the page, and the banner's window
           would show the same painting a third time. The title is carved into the
           ring's own sky instead. -->
      <p v-if="error" class="mb-4 text-sm text-error">{{ error }}</p>
      <p v-if="loading" class="text-text-dim">Loading&hellip;</p>

      <template v-if="!loading && stats">
        <!-- ── THE RING — you on one side, the foe on the other, one shared
             axis down the middle carrying the wager. BG.pit is the ground;
             nothing here is a tinted CSS panel standing in for painted art. -->
        <div class="hg-ring bound-frame relative overflow-hidden mb-4">
          <img :src="BG.pit" alt="" class="hg-floor" />
          <div class="hg-vignette" aria-hidden="true"></div>
          <div class="hg-frost" aria-hidden="true"></div>

          <!-- the title, carved into the ring's sky -->
          <div class="hg-title">
            <h1 class="gilt-text">{{ t('arena.title') }}</h1>
            <p>{{ t('arena.sub') }}</p>
          </div>

          <div class="hg-duel">
            <!-- YOU -->
            <div class="hg-side hg-side-you">
              <img v-if="youArt" :src="youArt" alt="" class="hg-pose hg-pose-you" />
              <div class="hg-nameplate">{{ stats.title }}</div>
              <!-- Your own level, mirroring the foe's plate (QA 15.08: the terms
                   named the opponent's level but never yours). Same key — the
                   two sides must read identically. -->
              <div class="hg-meta">{{ t('arena.foeLevel', { level: stats.level }) }} &middot; {{ t('arena.rating', { rating: stats.rating }) }}</div>
            </div>

            <!-- THE AXIS — wager, stakes, matchmaking, carved into the ring's edge -->
            <div class="hg-axis">
              <div class="hg-rune" aria-hidden="true">᛭</div>

              <template v-if="state === 'idle'">
                <button @click="enter" class="btn-forge btn-forge--rune btn-forge--lg">{{ t('arena.enter') }}</button>
                <p class="hg-axis-sub">{{ t('arena.firstBlood') }}</p>
                <!-- What it costs and what it pays, stated BEFORE the queue —
                     the lobby used to say nothing about either. -->
                <div class="hg-terms">
                  <span class="hg-term"><i>{{ t('arena.stakes.costs') }}</i><b>{{ stats.stakes.nerveCost }} {{ t('arena.stakes.fury') }}</b></span>
                  <!-- Both sides of the wager, points included — the terms used to
                       show only the win's "+15", as if losing were free (QA 18.08
                       items 18/19). -->
                  <span class="hg-term"><i>{{ t('arena.stakes.win') }}</i><b class="hg-term-win">+{{ stats.stakes.winGold.toLocaleString() }} {{ t('arena.stakes.silver') }} &middot; +{{ stats.stakes.winRating }} {{ t('arena.stakes.rating') }}</b></span>
                  <span class="hg-term"><i>{{ t('arena.stakes.lose') }}</i><b class="hg-term-loss">&minus;{{ stats.stakes.lossGold.toLocaleString() }} {{ t('arena.stakes.silver') }} &middot; &minus;{{ Math.abs(stats.stakes.lossRating) }} {{ t('arena.stakes.rating') }}</b></span>
                </div>
                <p v-if="flowError" class="hg-err">{{ flowError }}</p>
                <!-- What the last walk-out actually cost, said in the lobby the
                     player lands back in — a charge must never be something they
                     only discover in their balance. -->
                <p v-if="cancelledNotice" class="hg-note">{{ cancelledNotice }}</p>
              </template>

              <template v-else-if="state === 'searching'">
                <div class="hg-seek" role="progressbar" :aria-label="t('arena.searchingAria')">
                  <span class="hg-seek-sweep"></span>
                </div>
                <div class="hg-search-txt">{{ t('arena.searching', { seconds: searchSeconds }) }}</div>
                <!-- BOTH sides of the wager, same as the lobby. This block used
                     to quote only the loss — the win line vanished the moment
                     the search began, which read as "the hero is already losing"
                     (CU-86cbc65ny item 4). The found-state swaps in the foe's
                     exact numbers; until then the preview stands. -->
                <div class="hg-terms">
                  <span class="hg-term"><i>{{ t('arena.stakes.costs') }}</i><b>{{ stats.stakes.nerveCost }} {{ t('arena.stakes.fury') }}</b></span>
                  <span class="hg-term"><i>{{ t('arena.stakes.win') }}</i><b class="hg-term-win">+{{ stats.stakes.winGold.toLocaleString() }} {{ t('arena.stakes.silver') }} &middot; +{{ stats.stakes.winRating }} {{ t('arena.stakes.rating') }}</b></span>
                  <span class="hg-term"><i>{{ t('arena.stakes.lose') }}</i><b class="hg-term-loss">&minus;{{ stats.stakes.lossGold.toLocaleString() }} {{ t('arena.stakes.silver') }} &middot; &minus;{{ Math.abs(stats.stakes.lossRating) }} {{ t('arena.stakes.rating') }}</b></span>
                </div>
                <button @click="leaveQueue" class="btn-forge btn-forge--carved btn-forge--sm">{{ t('arena.leaveQueue') }}</button>
              </template>

              <template v-else-if="state === 'found' && opponent">
                <div class="chip-rune hg-wager">
                  <span>{{ t('arena.stake') }}</span>
                  <b>{{ t('arena.rating', { rating: opponent.rating }) }}</b>
                </div>
                <!-- The rating swing, from the preview — the same number the
                     settlement applies (QA 86cb8zdku). Season standing still
                     settles server-side but is not quoted: one ladder number,
                     always ELO (owner 2026-08-28). -->
                <div class="hg-terms">
                  <span class="hg-term"><i>{{ t('arena.stakes.costs') }}</i><b>{{ stats.stakes.nerveCost }} {{ t('arena.stakes.fury') }}</b></span>
                  <span class="hg-term"><i>{{ t('arena.stakes.win') }}</i><b class="hg-term-win">+{{ stats.stakes.winGold.toLocaleString() }} {{ t('arena.stakes.silver') }} &middot; +{{ opponent.winRating }} {{ t('arena.stakes.rating') }}</b></span>
                  <span class="hg-term"><i>{{ t('arena.stakes.lose') }}</i><b class="hg-term-loss">&minus;{{ stats.stakes.lossGold.toLocaleString() }} {{ t('arena.stakes.silver') }} &middot; &minus;{{ Math.abs(opponent.lossRating) }} {{ t('arena.stakes.rating') }}</b></span>
                </div>
                <!-- The accept window. The hold really does lapse server-side,
                     so the player is told how long they have (QA 86cb9yd3p
                     item 6) rather than finding out from a bounced FIGHT. -->
                <p class="hg-hold" :class="{ 'is-urgent': holdUrgent }" role="timer" aria-live="off">
                  {{ t('arena.holdLeft', { time: `${holdLeft.m}:${holdLeft.s}` }) }}
                </p>
                <p v-if="flowError" class="hg-err">{{ flowError }}</p>
                <div class="hg-cta-row">
                  <button :disabled="busy" @click="fight" class="btn-forge btn-forge--rune btn-forge--sm">{{ t('arena.fight') }}</button>
                  <!-- The fee is ON the button. Walking out on a foe you have
                       been shown costs a win's purse (CU-86cb9yd3p item 7), and
                       the one thing worse than charging for it is charging for
                       it silently. -->
                  <button :disabled="busy" @click="cancelFound" class="btn-forge btn-forge--carved btn-forge--sm">
                    {{ cancelRating > 0 ? t('arena.cancelWithRating', { rating: cancelRating }) : t('arena.cancel') }}
                  </button>
                </div>
              </template>
            </div>

            <!-- THE FOE — a challenger is ALWAYS standing there, a cold silhouette
                 until one is actually sworn, so this side is never a blank gap. -->
            <div class="hg-side hg-side-foe" :class="{ 'is-warm': state === 'found' && opponent }">
              <template v-if="state === 'found' && opponent">
                <img v-if="foeArt(opponent.heroClass)" :src="foeArt(opponent.heroClass)" :alt="classLabel(opponent.heroClass)" class="hg-pose hg-pose-foe" />
                <div class="hg-nameplate hg-nameplate-warm">{{ opponent.name }}</div>
                <div class="hg-meta">{{ classLabel(opponent.heroClass) }} &middot; {{ t('arena.foeLevel', { level: opponent.level }) }} &middot; {{ t('arena.rating', { rating: opponent.rating }) }}</div>
              </template>
              <template v-else>
                <img v-if="foeArt('berserker')" :src="foeArt('berserker')" alt="" class="hg-pose hg-pose-foe hg-pose-silhouette" />
                <div class="hg-nameplate hg-nameplate-dim">{{ t('arena.challengerWaits') }}</div>
                <div class="hg-meta">{{ t('arena.crowdHolds') }}</div>
              </template>
            </div>
          </div>
        </div>

        <!-- ── THE RECORD — three carved stones. The ring shows the current duel;
             this is the loud season-to-date line it has no room to shout. ────── -->
        <div class="hg-record mb-6">
          <div class="hg-record-stone">
            <div class="hg-record-num hg-record-num-win">{{ stats.wins }}</div>
            <div class="hg-record-lbl">{{ t('arena.wins') }}</div>
          </div>
          <div class="hg-record-stone">
            <div class="hg-record-num hg-record-num-cold">{{ stats.losses }}</div>
            <div class="hg-record-lbl">{{ t('arena.losses') }}</div>
          </div>
          <div class="hg-record-stone">
            <div class="hg-record-num" :class="stats.streak > 0 ? 'hg-record-num-streak' : 'hg-record-num-cold'">{{ stats.streak }}</div>
            <div class="hg-record-lbl">{{ stats.streak > 0 ? t('arena.winStreak') : t('arena.noStreak') }}</div>
          </div>
        </div>

        <!-- ── SAGA OF BOUTS — the ledger of what actually happened: loud winners,
             cold losses, nothing generic-list about it ─────────────────────── -->
        <div class="hg-saga bound-frame surf-plank relative">
          <h2 class="hg-saga-h"><span class="gilt-text">{{ t('arena.sagaTitle') }}</span></h2>
          <span class="hg-saga-divider" aria-hidden="true"></span>

          <ul v-if="stats.recent.length" class="hg-saga-list">
            <li
              v-for="(m, i) in stats.recent"
              :key="i"
              class="hg-bout-row"
              :class="[m.result === 'VICTORY' ? 'is-win' : 'is-loss', { 'is-alt': i % 2 === 1 }]"
            >
              <span class="hg-bout-mark" :class="m.result === 'VICTORY' ? 'is-win' : 'is-loss'">
                {{ m.result === 'VICTORY' ? 'ᚢ' : 'ᛞ' }}
              </span>
              <span class="hg-bout-info">
                <span class="hg-bout-name">{{ t('arena.vs', { opponent: m.opponent }) }}</span>
                <span class="hg-bout-sub" :class="m.result === 'VICTORY' ? 'is-win' : 'is-loss'">
                  {{ m.result === 'VICTORY' ? t('arena.victory') : t('arena.defeat') }}
                </span>
              </span>
              <span class="hg-bout-change">
                <span class="hg-bout-num" :class="m.ratingChange >= 0 ? 'is-win' : 'is-loss'">
                  {{ m.ratingChange >= 0 ? '+' : '−' }}{{ Math.abs(m.ratingChange) }}
                </span>
                <span class="hg-bout-num-lbl">{{ t('arena.ratingLabel') }}</span>
              </span>
            </li>
          </ul>

          <div v-else class="hg-saga-empty">
            <p class="hg-saga-empty-txt">{{ t('arena.empty') }}</p>
          </div>
        </div>
      </template>
    </template>
  </section>
</template>

<style scoped>
/* ── THE RING ────────────────────────────────────────────────────────────
   BG.pit is the ground — real painted art, full bleed, not a tinted CSS
   panel standing in for it. A vignette (symmetric, edge-to-edge) keeps the
   duel legible without ever boxing a title into one corner over a photo.
   The ring is sized to its OWN floor now (min(56vh, 480px)) instead of a
   height derived from the poses — `.hg-duel`'s `align-items: stretch`
   already keeps the two sides and the axis level with each other, so no
   shared `--hg-h` constant is needed to keep them in sync. ─────────────── */
.hg-ring {
  min-height: min(62vh, 560px);
}
/* the page's title, carved into the ring's own sky — no separate banner
   repeating the same painting above it */
.hg-title {
  position: absolute;
  inset-inline: 0;
  top: 26px;
  z-index: 2;
  text-align: center;
  pointer-events: none;
}
.hg-title h1 {
  font-family: var(--font-heading);
  font-size: clamp(26px, 3vw, 38px);
  line-height: 1;
}
.hg-title p {
  margin-top: 6px;
  font-family: var(--font-label);
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #b8c7d8;
  text-shadow: 0 2px 5px rgba(0, 0, 0, 0.9);
}
.hg-floor {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  object-position: center 38%;
}
.hg-vignette {
  position: absolute; inset: 0;
  pointer-events: none;
  background:
    radial-gradient(120% 85% at 50% 8%, rgba(6, 9, 16, 0.1), rgba(4, 6, 11, 0.5) 78%),
    linear-gradient(180deg, rgba(6, 9, 16, 0.12) 0%, rgba(5, 8, 14, 0.42) 55%, rgba(4, 6, 11, 0.85) 100%);
}
/* frost hanging off the ring's top edge, same device as scr-head-win's glass */
.hg-frost {
  position: absolute; inset-inline: 0; top: 0; height: 15px;
  pointer-events: none;
  background: var(--orn-frost-ice) repeat-x left top;
  opacity: 0.85;
}

.hg-duel {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: stretch;
  gap: clamp(6px, 3vw, 26px);
  padding: 30px clamp(10px, 4vw, 40px) 16px;
}

/* YOU / FOE — identical rule for both sides; the grid's `align-items: stretch`
   (above) keeps the three columns level, not a shared min-height constant. */
.hg-side {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
  text-align: center;
}
.hg-axis {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
  padding: 0 4px 2px;
  text-align: center;
}

.hg-rune {
  font-family: var(--font-heading);
  font-size: 34px;
  line-height: 1;
  color: var(--color-aurora);
  text-shadow: 0 0 18px color-mix(in srgb, var(--color-aurora) 70%, transparent), 0 2px 6px rgba(0, 0, 0, 0.85);
}

/* below the Enter button — a carved footnote, not another button */
.hg-axis-sub {
  font-family: var(--font-label);
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #a9b9cb;
  text-shadow: 0 2px 5px rgba(0, 0, 0, 0.9), 0 0 10px rgba(4, 6, 11, 0.7);
}

/* THE TERMS — what a bout costs and pays, carved small under the call to
   arms. Reads on the painted ring floor, so every line carries its own
   shadow rather than sitting on a panel. */
.hg-terms {
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: center;
  margin-top: 2px;
}
.hg-term {
  display: inline-flex;
  align-items: baseline;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  font-family: var(--font-label);
  font-size: 10px;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  text-shadow: 0 2px 5px rgba(0, 0, 0, 0.95), 0 0 10px rgba(4, 6, 11, 0.8);
}
.hg-term i { font-style: normal; color: #8fa2b6; }
.hg-term b { font-weight: 400; color: #dbe5ef; }
.hg-term-win { color: #8fd0a6; }
.hg-term-loss { color: #d9899a; }

/* the poses are the whole point of the ring now — big enough that the floor
   actually reads as a duel rather than a backdrop with a button on it */
.hg-pose {
  height: clamp(200px, 34vw, 340px);
  width: auto;
  object-fit: contain;
  filter: drop-shadow(0 14px 18px rgba(0, 0, 0, 0.7));
}
/* facing right, into the ring */
.hg-pose-you { transform: scaleX(1); }
/* mirrored to face left, into the ring — same convention FightScreen uses */
.hg-pose-foe { transform: scaleX(-1); }
/* the idle-side challenger — a figure in the torch-shadow, not a person yet.
   brightness(.12) read as NOTHING on the dark art; he has to be SEEN waiting,
   so: darkened but present, drained of colour, a cold aurora rim to cut him
   off the crowd behind. */
.hg-pose-silhouette {
  filter: brightness(0.42) saturate(0.12) contrast(1.06)
    drop-shadow(0 0 16px rgba(111, 216, 192, 0.30))
    drop-shadow(0 10px 14px rgba(0, 0, 0, 0.6));
  opacity: 0.9;
}

/* ── NAMEPLATE — an iron tag riveted under each fighter, not a floating
   text label sitting loose on the art. ──────────────────────────────────── */
.hg-nameplate {
  clip-path: var(--clip-hewn);
  background: var(--mat-iron);
  padding: 4px 12px;
  font-family: var(--font-heading);
  font-size: clamp(15px, 1.7vw, 17px);
  line-height: 1.15;
  color: #dfe7f1;
  text-shadow: 0 1px 0 rgba(0, 0, 0, 0.9), 0 0 10px rgba(4, 6, 11, 0.7);
  box-shadow: inset 0 1px 0 rgba(226, 236, 248, 0.12), inset 0 0 0 1px rgba(0, 0, 0, 0.55);
}
.hg-nameplate-warm {
  box-shadow:
    inset 0 1px 0 rgba(226, 236, 248, 0.12),
    inset 0 0 0 1px rgba(0, 0, 0, 0.55),
    inset 0 0 14px color-mix(in srgb, var(--color-ember) 35%, transparent);
}
.hg-nameplate-dim { color: #b7c4d3; }

.hg-meta {
  font-family: var(--font-label);
  font-size: 10.5px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #a9b9cb; /* text-dim fails on the ring art; this clears 4.5:1 with the shadow */
  text-shadow: 0 2px 5px rgba(0, 0, 0, 0.85);
}

/* Indeterminate seek bar. The old one was a .bar-shield filling toward the
   rolled hold, so its speed announced the queue length before the first second
   was out, and it stepped once per tick because the fill was driven by a
   whole-second counter. This sweeps at a fixed rate: smooth, and it carries no
   duration information at all. */
.hg-seek {
  position: relative;
  overflow: hidden;
  width: min(170px, 100%);
  height: 14px;
  background: linear-gradient(180deg, #0d1a27 0%, #16232f 60%, #0d1a27 100%);
  box-shadow:
    inset 0 2px 4px rgba(0, 0, 0, 0.85),
    inset 0 -1px 0 rgba(226, 236, 248, 0.06);
}
.hg-seek-sweep {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 42%;
  background: linear-gradient(90deg, transparent 0%, var(--color-aurora) 48%, transparent 100%);
  opacity: 0.85;
  animation: hg-seek-run 1.35s ease-in-out infinite alternate;
}
@keyframes hg-seek-run {
  from { transform: translateX(-45%); }
  to   { transform: translateX(180%); }
}
@media (prefers-reduced-motion: reduce) {
  .hg-seek-sweep { animation-duration: 3.2s; }
}
.hg-search-txt {
  font-family: var(--font-label);
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text);
  text-shadow: 0 2px 5px rgba(0, 0, 0, 0.85);
}

.hg-wager {
  flex-direction: column;
  gap: 0.15em;
  padding: 8px 16px;
}
.hg-wager b {
  font-family: var(--font-heading);
  font-size: 16px;
  color: var(--color-gold);
  text-transform: none;
  letter-spacing: 0;
}

.hg-cta-row { display: flex; gap: 8px; }
.hg-err { font-size: 12px; color: var(--color-error); max-width: 190px; }
/* A receipt, not a failure — the walk-out fee that was just charged. Dimmer
   than .hg-err on purpose: nothing went wrong, the player was told the price
   on the button and paid it. */
.hg-note { font-size: 12px; color: var(--color-text-dim); max-width: 190px; }

/* The accept window on a found match. Quiet while there is time, warm when the
   hold is nearly out — it must read as a deadline, not decoration. */
.hg-hold {
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: color-mix(in srgb, var(--color-text-dim) 85%, transparent);
  font-variant-numeric: tabular-nums;
}
.hg-hold.is-urgent { color: var(--color-error); }

/* ── THE RECORD — three runestones, wins/losses/streak, loud enough to read
   as a stat line rather than a caption under the ring. ───────────────────── */
.hg-record {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
.hg-record-stone {
  clip-path: var(--clip-stele);
  background: var(--tex-stone), var(--sunken);
  background-size: 150px 150px, auto;
  background-blend-mode: soft-light, normal;
  box-shadow: var(--rim-ice-sunken);
  padding: 16px 8px 12px;
  text-align: center;
}
.hg-record-num {
  font-family: var(--font-heading);
  font-size: 30px;
  line-height: 1;
}
.hg-record-num-win {
  color: var(--color-ice);
  text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.85), 0 1px 0 rgba(207, 232, 255, 0.2);
}
.hg-record-num-cold {
  color: var(--color-text-dim);
  text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.85);
}
.hg-record-num-streak {
  color: var(--color-ember);
  text-shadow: 0 0 14px color-mix(in srgb, var(--color-ember) 60%, transparent), 0 -1px 0 rgba(0, 0, 0, 0.85);
}
.hg-record-lbl {
  margin-top: 4px;
  font-family: var(--font-label);
  font-size: 9px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9db0c6;
}

/* ── PHONE — the desktop ring is three columns (you · axis · foe), and the
   axis alone (the Enter button) is ~230px wide: two poses + the axis simply
   do not fit across 390px, so the duel restacks — the two fighters stay
   face-to-face on top, and the axis (wager, buttons, search bar) takes the
   full width of the ring floor below them. ─────────────────────────────── */
@media (max-width: 640px) {
  .hg-duel {
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      'you  foe'
      'axis axis';
    /* top padding clears the title carved into the ring's sky, which is
       absolute and no longer has a wide middle column to hover over */
    padding: 78px 6px 12px;
    gap: 14px 4px;
  }
  .hg-side-you { grid-area: you; }
  .hg-side-foe { grid-area: foe; }
  .hg-axis { grid-area: axis; justify-content: center; padding-bottom: 4px; }
  .hg-pose { height: clamp(140px, 40vw, 200px); }
  .hg-nameplate { font-size: 13.5px; padding: 3px 9px; }
  .hg-meta { font-size: 9px; }
  .hg-cta-row { flex-direction: row; } /* full-width row now — no need to stack */
  .hg-record-num { font-size: 22px; }
  .hg-saga { padding: 14px 12px 6px; }
}

/* ── SAGA OF BOUTS — a carved ledger: a struck gold title, then plank rows
   that alternate, with victories lit and losses run cold. Replaces the old
   generic "Recent bouts" list. ───────────────────────────────────────────── */
.hg-saga { padding: 18px 20px 8px; }
.hg-saga-h {
  font-family: var(--font-heading);
  font-size: 18px;
  line-height: 1.1;
}
.hg-saga-divider {
  display: block;
  width: 170px;
  height: 9px;
  margin: 6px 0 10px;
  background: var(--orn-divider-gold) no-repeat left center / contain;
}

.hg-saga-list { list-style: none; margin: 0; padding: 0; }
.hg-bout-row {
  position: relative;
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  padding: 4px 8px;
}
.hg-bout-row.is-alt { background: rgba(0, 0, 0, 0.16); }
/* a lit edge on victories only — the one thing a scanning eye should catch first */
.hg-bout-row.is-win::before {
  content: '';
  position: absolute;
  inset-block: 0;
  inset-inline-start: 0;
  width: 3px;
  background: linear-gradient(180deg, transparent, rgba(217, 135, 60, 0.75), transparent);
}

.hg-bout-mark {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  clip-path: var(--clip-hewn);
  font-family: var(--font-heading);
  font-size: 17px;
  line-height: 1;
}
.hg-bout-mark.is-win {
  background: radial-gradient(circle, rgba(200, 162, 76, 0.28), rgba(10, 14, 20, 0.85) 75%);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--color-gold) 50%, transparent);
  color: var(--color-gold-bright);
  text-shadow: 0 0 10px color-mix(in srgb, var(--color-gold-bright) 60%, transparent);
}
.hg-bout-mark.is-loss {
  background: rgba(9, 14, 22, 0.8);
  box-shadow: inset 0 0 0 1px rgba(90, 110, 135, 0.35);
  color: #91a3b8;
}

.hg-bout-info { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.hg-bout-name {
  font-family: var(--font-heading);
  font-size: 15px;
  line-height: 1.15;
  color: #dfe7f1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hg-bout-sub {
  font-family: var(--font-label);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.hg-bout-sub.is-win { color: #d9b96a; }
.hg-bout-sub.is-loss { color: var(--color-text-dim); }

.hg-bout-change { display: flex; flex-direction: column; align-items: flex-end; gap: 1px; }
.hg-bout-num {
  font-family: var(--font-heading);
  font-size: 19px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}
.hg-bout-num.is-win {
  color: var(--color-gold-bright);
  text-shadow: 0 0 10px color-mix(in srgb, var(--color-gold-bright) 55%, transparent);
}
/* --color-error (#b3263a) reads fine on a button plate but drops under 4.5:1 on
   this panel's lighter plank surface — this dusty rose is the same "cold loss"
   hue, tuned to clear contrast against --mat-plank (verified ~4.7:1). */
.hg-bout-num.is-loss { color: #c0717a; }
.hg-bout-num-lbl {
  font-family: var(--font-label);
  font-size: 9px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9db0c6;
}

.hg-saga-empty {
  position: relative;
  isolation: isolate;
  display: grid;
  place-items: center;
  min-height: 96px;
  padding: 12px 0 16px;
}
.hg-saga-empty::before {
  content: '᛭';
  position: absolute;
  z-index: -1;
  font-family: var(--font-heading);
  font-size: 80px;
  color: rgba(160, 190, 220, 0.06);
}
.hg-saga-empty-txt {
  font-family: var(--font-body);
  font-style: italic;
  font-size: 13px;
  color: #bccbdb;
}

@media (prefers-reduced-motion: reduce) {
  .bar-shield-fill { transition: none; }
}
</style>
